<script lang="ts">
enum ProjectTab {
  REPORTS = "reports",
  DRAWINGS = "drawings",
}
</script>

<script setup lang="ts">
import { computed, watch } from "vue";

import { useRoute, useRouter } from "vue-router";
import { useGetProject } from "@/stores/queries";
import ProjectDrawingsSection from "@/components/projects/drawings-sections/ProjectDrawingsSection.vue";
import ProjectStatusSelector from "@/components/projects/ProjectStatusSelector.vue";
import ProjectActionsDropdown from "@/components/projects/ProjectActionsDropdown.vue";

import { useDeviceState } from "@/stores/DeviceState";
import { storeToRefs } from "pinia";
import ProjectReportsSection from "@/components/diagnostics/ProjectReportsSection.vue";
import EditableProjectAvatar from "@/components/projects/EditableProjectAvatar.vue";
import $ls, { LocalStorageKey } from "@/utils/local-storage";
import { useTranslation } from "i18next-vue";

const { t } = useTranslation();
const deviceState = useDeviceState();
const { isOnline } = storeToRefs(deviceState);

const route = useRoute();
const router = useRouter();

const projectId = computed(() => {
  return route.params.id ? Number(route.params.id) : undefined;
});

const isValidTab = (value: string | undefined | null): value is ProjectTab =>
  Object.values(ProjectTab).includes(value as ProjectTab);

const getInitialTab = (): ProjectTab => {
  const queryTab = route.query.tab as string | undefined;
  if (isValidTab(queryTab)) return queryTab;

  const storedTab = $ls.getItem(LocalStorageKey.PROJECT_ACTIVE_TAB);
  if (isValidTab(storedTab)) return storedTab;

  return ProjectTab.REPORTS;
};

const activeTab = computed<ProjectTab>({
  get: () => {
    const queryTab = route.query.tab as string | undefined;
    if (isValidTab(queryTab)) return queryTab;

    const storedTab = $ls.getItem(LocalStorageKey.PROJECT_ACTIVE_TAB);
    if (isValidTab(storedTab)) return storedTab;

    return ProjectTab.REPORTS;
  },
  set: (tab: ProjectTab) => {
    $ls.setItem(LocalStorageKey.PROJECT_ACTIVE_TAB, tab);
    router.replace({ query: { ...route.query, tab } });
  },
});

// Sync URL query on initial load if missing
watch(
  () => route.params.id,
  () => {
    if (!route.query.tab) {
      const initial = getInitialTab();
      router.replace({ query: { ...route.query, tab: initial } });
    }
  },
  { immediate: true }
);

const { data: project } = useGetProject(projectId);
</script>

<template>
  <div class="my-5 mx-3 mx-md-4 mx-lg-5 px-sm-2 px-md-4 px-lg-5">
    <div
      v-if="project"
      class="mb-2 d-flex flex-row gap-3 align-items-center pb-4"
    >
      <EditableProjectAvatar :readonly="!isOnline" :project="project" />
      <div class="h2 text-truncate">
        {{ project.name }}
      </div>
      <ProjectStatusSelector :project="project" :disabled="!isOnline" />
      <div>
        <ProjectActionsDropdown
          :project="project"
          :readonly="!isOnline"
          hide-open-option
        />
      </div>
    </div>

    <el-tabs v-if="projectId" v-model="activeTab">
      <el-tab-pane
        :label="t('dashboard:project.tabs.reports')"
        :name="ProjectTab.REPORTS"
      >
        <ProjectReportsSection
          :project-id="projectId"
          :readonly="!isOnline"
          :projectColor="project?.color"
        />
      </el-tab-pane>
      <el-tab-pane
        :label="t('dashboard:project.tabs.drawings')"
        :name="ProjectTab.DRAWINGS"
      >
        <ProjectDrawingsSection
          :project-id="projectId"
          :readonly="!isOnline"
          :projectColor="project?.color"
        />
      </el-tab-pane>
    </el-tabs>
  </div>
</template>
